/**
 * =========================================================================================
 *     Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   webapp/frontend/src/utils/slaCompliance.ts
 *     Description     :   Spec 292 D5 (owner, 2026-10-28: "503 and the tile's unavailable state. A zero is never
 *                         shown for a failed read"). The ONE reading of /api/health/sla the Admin and Manager
 *                         dashboards share: a failed read is unavailable (the tile shows "—" and says so), rows
 *                         average their compliance_percentage, a scalar currentUptime passes through, an empty
 *                         successful answer is a measured 0.
 *     Author          :   Claude Fable 5.1
 *     Created         :   2026-10-28
 * =========================================================================================
 */

export interface SlaComplianceReading {
  /** Month-to-date compliance in percent, or null when the read failed. */
  slaCompliance: number | null;
  /** True when /api/health/sla answered success: false (503): the tile renders "—" and "SLA status could not be read". */
  slaUnavailable: boolean;
}

type SlaRow = { compliance_percentage?: number | null };
type SlaResponse = { success?: boolean; data?: unknown; error?: string };

export function slaComplianceFrom(slaRes: SlaResponse): SlaComplianceReading {
  if (slaRes.success === false) return { slaCompliance: null, slaUnavailable: true };
  const slaData = slaRes.data;
  // slaRepository.getStatus() returns SlaStatusRow[] — average compliance_percentage across the rows.
  if (Array.isArray(slaData) && slaData.length > 0) {
    const rows = slaData as SlaRow[];
    return { slaCompliance: Math.round(rows.reduce((sum, r) => sum + (r.compliance_percentage ?? 0), 0) / rows.length), slaUnavailable: false };
  }
  const scalar = (slaData as { currentUptime?: unknown } | null | undefined)?.currentUptime;
  if (typeof scalar === 'number') return { slaCompliance: scalar, slaUnavailable: false };
  return { slaCompliance: 0, slaUnavailable: false };
}
